/**
 * Workflow-step CRUD mutation hooks for the AC-1377 React Query migration
 * (AC-1403 / Phase 3.2).
 *
 * Implements the contract from the Technical Design at
 * `docs/technical/AC-1377-react-query-technical-design.md`:
 *   - Section 2.6 — mutation + invalidation contract (step rows)
 *   - Section 5.3 — mutation hook signatures
 *   - Section 8.2 — Phase 3 verification table
 *
 * ## Why this exists
 *
 * Step CRUD currently happens via direct `apiService.{createWorkflowStep,
 * updateWorkflowStep,deleteWorkflowStep}` calls scattered across
 * `WorkflowEditor.tsx` and the per-node-type modals (AgentNode,
 * ConditionalNode, FilterNode, QueueReadNode, QueueWriteNode, UserPromptNode,
 * AutoPromptNode). Each call site is followed by a `loadWorkflow(...)`
 * reload to refresh the canvas. After AC-1401, that reload is a thin
 * invalidation shim, but the call site still owns the invalidation
 * contract — which is exactly the shape that produced the AC-1390 /
 * AC-1391 / AC-1394 stale-modal-data class of bugs.
 *
 * These hooks centralize the contract: every step mutation knows it must
 * invalidate `[workflow, id, steps]` AND `[workflow, id, execution]`, and
 * call sites get atomic "edit step → canvas + execution panel both refresh"
 * semantics. The legacy direct-`apiService` paths inside `WorkflowEditor`
 * are NOT migrated by this ticket — that's AC-1406. AC-1405 (step drag
 * optimistic position) extends the mutation surface with `onMutate`
 * snapshot/restore; this ticket is intentionally pessimistic.
 *
 * ## Why workflowId at hook level (not mutate-time)
 *
 * Unlike workflow CRUD (where the id changes per call), step CRUD is
 * always scoped to the SAME workflow for the lifetime of the editor session
 * — the user has navigated to one workflow's canvas, and every step edit
 * targets that workflow's id. Closing the workflowId at hook construction
 * time gives us:
 *
 *   1. Cleaner call sites: `mutate({ stepId, data })` instead of
 *      `mutate({ workflowId, stepId, data })` at every step modal.
 *   2. A single source of truth for which workflow's keys to invalidate —
 *      the mutation can't accidentally invalidate the wrong workflow's
 *      cache because the id is captured in the closure.
 *   3. Symmetric API with `useWorkflow(id)` / `useWorkflowSteps(id)` read
 *      hooks, which also take id at hook level.
 *
 * ## Invalidation strategy
 *
 * On every step mutation we invalidate BOTH:
 *
 *   - `queryKeys.workflowSteps(workflowId)` — the canvas needs to rebuild
 *     because step list / wiring / labels may have changed.
 *   - `queryKeys.workflowExecution(workflowId)` — the execution panel
 *     renders step labels next to run results; if a step was renamed or
 *     deleted, those labels must update too. (TD section 2.6 calls this
 *     out explicitly: "step labels can appear in execution".)
 *
 * Note that `queryKeys.workflow(workflowId)` itself (the detail row) is
 * NOT invalidated by step CRUD — the detail row is the workflow's
 * metadata (name, description, project_id, settings), which step CRUD
 * does not touch. Invalidating it would cause an unnecessary detail
 * refetch on every step edit.
 *
 * If a future change requires step edits to refresh the workflow detail
 * (e.g. denormalized step counts on the workflow row), add the third
 * invalidation here and update TD section 2.6.
 *
 * ## API
 *
 *   const create = useCreateStep(workflowId);
 *   await create.mutateAsync({ step_type: 'run_agent', step_order: 0 });
 *
 *   const update = useUpdateStep(workflowId);
 *   await update.mutateAsync({ stepId: '42', data: { label: 'Renamed' } });
 *
 *   const remove = useDeleteStep(workflowId);
 *   await remove.mutateAsync({ stepId: '42' });
 */

import {
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';

import apiService from '../../services/api';
import { WorkflowStep } from '../../types';
import { queryKeys } from './queryKeys';

/**
 * Create-step input. `data` is a `Partial<WorkflowStep>` because
 * `apiService.createWorkflowStep` is permissive (the server fills in
 * defaults for omitted fields). The `orchestration_id` field is set by
 * `apiService.createWorkflowStep` itself — callers do not need to thread
 * it through.
 */
export type CreateStepVariables = Partial<WorkflowStep>;

/**
 * Update-step input. The stepId is the canonical string-form id from the
 * server (`orchestration_step_id`). `data` is a partial because in practice
 * step modals send only the fields they touched.
 */
export type UpdateStepVariables = {
  stepId: string | number;
  data: Partial<WorkflowStep>;
};

/**
 * Delete-step input. stepId only — the workflowId is in the closure.
 */
export type DeleteStepVariables = {
  stepId: string | number;
};

/**
 * Helper: invalidate the two step-related keys for a given workflowId.
 *
 * Extracted to a private helper so all three mutations stay in sync if the
 * invalidation contract grows (e.g. add `workflow(id)` if step CRUD ever
 * starts mutating workflow-level metadata).
 */
function invalidateStepKeys(
  queryClient: ReturnType<typeof useQueryClient>,
  workflowId: string | number,
): void {
  queryClient.invalidateQueries({
    queryKey: queryKeys.workflowSteps(workflowId),
  });
  queryClient.invalidateQueries({
    queryKey: queryKeys.workflowExecution(workflowId),
  });
}

/**
 * `useCreateStep(workflowId)` — mutation hook for
 * `POST /api/v1/orchestrations/:workflowId/steps`.
 *
 * @param workflowId The workflow that owns the new step. Captured in
 *                   closure so the mutation always invalidates the right
 *                   workflow's cache.
 *
 * @example
 *   const create = useCreateStep(workflowId);
 *   await create.mutateAsync({ step_type: 'run_agent', step_order: 0 });
 */
export function useCreateStep(
  workflowId: string | number,
): UseMutationResult<WorkflowStep, Error, CreateStepVariables> {
  const queryClient = useQueryClient();
  return useMutation<WorkflowStep, Error, CreateStepVariables>({
    mutationFn: (data) =>
      apiService.createWorkflowStep(workflowId as string, data),
    onSuccess: (created) => {
      // AC-1582: append the new step to the workflowSteps cache so the
      // canvas seed effect renders it without waiting for the background
      // refetch. Defensive: only append when the response has an id and
      // isn't already in the list (idempotent if the refetch raced ahead).
      if (created && (created as any).orchestration_step_id != null) {
        const key = queryKeys.workflowSteps(workflowId);
        queryClient.setQueryData<WorkflowStep[]>(key, (prev) => {
          if (!Array.isArray(prev)) return prev;
          const id = (created as any).orchestration_step_id;
          if (prev.some((s) => (s as any).orchestration_step_id === id)) {
            return prev;
          }
          return [...prev, created];
        });
      }
      invalidateStepKeys(queryClient, workflowId);
    },
  });
}

/**
 * `useUpdateStep(workflowId)` — mutation hook for
 * `PUT /api/v1/orchestrations/steps/:stepId`.
 *
 * @param workflowId The workflow that owns the step being updated.
 *
 * @example
 *   const update = useUpdateStep(workflowId);
 *   await update.mutateAsync({ stepId: '42', data: { label: 'Renamed' } });
 */
export function useUpdateStep(
  workflowId: string | number,
): UseMutationResult<WorkflowStep, Error, UpdateStepVariables> {
  const queryClient = useQueryClient();
  return useMutation<WorkflowStep, Error, UpdateStepVariables>({
    mutationFn: ({ stepId, data }) =>
      apiService.updateWorkflowStep(stepId as string, data),
    onSuccess: (updated) => {
      // AC-1582: splice the response straight into the workflowSteps cache
      // so subscribers (canvas seed effect, modal that may immediately
      // reopen) see fresh data synchronously. Invalidation alone left a
      // window between the PUT response and the refetch completing where
      // observers still saw stale data — surfaced first as a per-step
      // field appearing empty in the editor until a hard refresh.
      if (updated && (updated as any).orchestration_step_id != null) {
        const key = queryKeys.workflowSteps(workflowId);
        queryClient.setQueryData<WorkflowStep[]>(key, (prev) => {
          if (!Array.isArray(prev)) return prev;
          const id = (updated as any).orchestration_step_id;
          return prev.map((s) =>
            (s as any).orchestration_step_id === id ? updated : s,
          );
        });
      }
      // Belt-and-suspenders: still invalidate so a background refetch
      // catches anything the response didn't carry (e.g. computed fields
      // or downstream consequences).
      invalidateStepKeys(queryClient, workflowId);
    },
  });
}

/**
 * `useDeleteStep(workflowId)` — mutation hook for
 * `DELETE /api/v1/orchestrations/steps/:stepId`.
 *
 * @param workflowId The workflow that owned the step being deleted.
 *
 * @example
 *   const remove = useDeleteStep(workflowId);
 *   await remove.mutateAsync({ stepId: '42' });
 */
export function useDeleteStep(
  workflowId: string | number,
): UseMutationResult<void, Error, DeleteStepVariables> {
  const queryClient = useQueryClient();
  return useMutation<void, Error, DeleteStepVariables>({
    mutationFn: ({ stepId }) =>
      apiService.deleteWorkflowStep(stepId as string),
    onSuccess: () => {
      invalidateStepKeys(queryClient, workflowId);
    },
  });
}
